import { useContext } from 'react';
import {
  Text,
  Paper,
  RingProgress,
  Title,
  Stack,
  Box,
  Flex,
  Loader,
} from '@mantine/core';
import { sentimentColors, sentimentIcons } from 'constants/reporting';
import { Sentiment } from 'interfaces/Reporting';

import classes from './CallSentiments.module.scss';
import { convertDecimalToPercentage } from 'utils/number';
import ReportContext from 'context/ReportContext';
import {
  getHighestSentiment,
  getValidSentiments,
  sentimentLabels,
} from '../utils';

const CallSentiments = () => {
  const {
    data: { callStats, isLoadingCallStats },
  } = useContext(ReportContext);

  const sentiments = isLoadingCallStats ? null : callStats.sentiment;

  const validSentiments = getValidSentiments(sentiments);
  const [sentiment] = getHighestSentiment(validSentiments);
  const SentimentSmiley = sentimentIcons[sentiment];

  const sections = [
    {
      value: convertDecimalToPercentage(validSentiments[Sentiment.POSITIVE]),
      color: sentimentColors[Sentiment.POSITIVE],
    },
    {
      value: convertDecimalToPercentage(validSentiments[Sentiment.NEUTRAL]),
      color: sentimentColors[Sentiment.NEUTRAL],
    },
    {
      value: convertDecimalToPercentage(validSentiments[Sentiment.NEGATIVE]),
      color: sentimentColors[Sentiment.NEGATIVE],
    },
  ];

  return (
    <Paper withBorder p={{ base: 'md', lg: 'lg' }} radius="md" h="100%">
      <Title size="1.5rem" mb="xl" className={classes.title}>
        Call sentiments
      </Title>
      <Text size="sm" c="dimmed" mb="lg" className={classes.description}>
        Calls completed by Receptionist
      </Text>
      
      <Flex direction="column" align="center">
        <Paper
          withBorder
          p="md"
          radius="md"
          bg="var(--bg-color)"
          w="100%"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
          <RingProgress
            size={250}
            thickness={20}
            sections={sections}
            label={
              isLoadingCallStats ? (
                <Flex align="center" direction="column" gap="0.25rem">
                  <Loader
                    size="md"
                    type="dots"
                    color="var(--mantine-color-gray-3)"
                  />
                </Flex>
              ) : callStats.num_call > 0 ? (
                <Flex align="center" direction="column" gap="0.25rem">
                  <SentimentSmiley
                    size={24}
                    stroke={1.5}
                    color={sentimentColors[sentiment]}
                  />
                  <Stack ta="center" gap={0}>
                    <Text size="xs" fw={700}>
                      Current trend
                    </Text>
                    <Text size="xs" c="dimmed">
                      {sentimentLabels[sentiment]}
                    </Text>
                  </Stack>
                </Flex>
              ) : (
                <Flex align="center" direction="column" gap="0.25rem">
                  <Text size="xs" c="dimmed">
                    No calls completed
                  </Text>
                </Flex>
              )
            }
          />
          <Flex align="center" gap={{ base: 'md', lg: 'xl' }} mb="xs">
            {Object.entries(validSentiments).map(([key]) => (
              <Flex
                key={key}
                align="center"
                gap={{ base: '0.5rem', lg: '0.75rem' }}
              >
                <Box
                  w={{ base: 10, lg: 12 }}
                  h={{ base: 10, lg: 12 }}
                  style={{
                    backgroundColor: sentimentColors[key as Sentiment],
                  }}
                />
                <Text
                  className={classes.label}
                  size="sm"
                  c="dimmed"
                  style={{ textTransform: 'capitalize' }}
                >
                  {sentimentLabels[key as Sentiment]}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Paper>
        <Flex justify="space-between" w="calc(90% - 4rem)" maw="100%" mt="lg">
          {Object.entries(validSentiments).map(([key, data]) => (
            <Flex key={key} direction="column" gap={4}>
              <Text
                size="sm"
                c="dimmed"
                className={classes.label}
                style={{ textTransform: 'capitalize' }}
              >
                {sentimentLabels[key as Sentiment]}
              </Text>
              <Text size="1.5rem" fw={700} className={classes.percentage}>
                {isLoadingCallStats ? (
                  <Loader
                    size="md"
                    type="dots"
                    h={24}
                    color="var(--mantine-color-gray-3)"
                  />
                ) : (
                  `${convertDecimalToPercentage(data)}%`
                )}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Paper>
  );
};

export default CallSentiments;
